import React from "react";
import { Modal } from "reactstrap";

/**
 * Shared building blocks for the operations console.
 *
 * Every screen in the handoff is assembled from the same few shapes — a white
 * rounded panel, a KPI tile, a pill toggle, a quiet data table, status chips.
 * They live here so 25 screens don't each re-derive the same paddings and
 * hexes, and so a change to the design lands in one place.
 *
 * Measurements are lifted from the prototype's inline styles.
 */

export const C = {
  brown: "#3d2817",
  brownDeep: "#2b2018",
  ink: "#1c1c1c",
  slate: "#5c5148",
  taupe: "#7b6552",
  taupeLight: "#a3917c",
  sand: "#d9c3a5",
  canvas: "#f7ecdc",
  surface: "#fbf5ec",
  surfaceAlt: "#fbf3e7",
  panel: "#f5eee3",
  tint: "#f0e5d5",
  iconTint: "#f4ead9",
  border: "#ecdfca",
  green: "#4caf50",
  red: "#c0202b",
  amber: "#e0a53f",
  bronze: "#b98a54",
};

/** White rounded panel — the container almost every block sits in. */
export const Panel = ({ title, subtitle, actions, children, style, bodyStyle }) => (
  <div
    style={{
      background: "#fff",
      borderRadius: 18,
      border: `1px solid ${C.tint}`,
      padding: 22,
      ...style,
    }}
  >
    {(title || actions) && (
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
        <div>
          {title && <div style={{ fontSize: 17, fontWeight: 500, color: C.ink }}>{title}</div>}
          {subtitle && <div style={{ marginTop: 4, fontSize: 13, color: C.taupeLight }}>{subtitle}</div>}
        </div>
        {actions && <div style={{ display: "flex", alignItems: "center", gap: 10 }}>{actions}</div>}
      </div>
    )}
    <div style={bodyStyle}>{children}</div>
  </div>
);

/**
 * KPI tile. `delta` is optional and colours itself: green when it reads as a
 * rise, red when it falls — a trend with no direction is just a number.
 */
export const StatCard = ({ icon, value, label, delta }) => {
  const down = typeof delta === "string" && delta.trim().startsWith("-");
  return (
    <div style={{ background: "#fff", borderRadius: 18, padding: 20, border: `1px solid ${C.tint}` }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span
          className="ms"
          style={{ fontSize: 22, color: C.brown, background: C.iconTint, padding: 9, borderRadius: 12 }}
        >
          {icon}
        </span>
        {delta != null && delta !== "" && (
          <span
            style={{
              display: "flex", alignItems: "center", gap: 3,
              fontSize: 13, fontWeight: 500, color: down ? C.red : C.green,
            }}
          >
            <span className="ms" style={{ fontSize: 17 }}>{down ? "trending_down" : "trending_up"}</span>
            {delta}
          </span>
        )}
      </div>
      <div style={{ marginTop: 16, fontSize: 34, fontWeight: 500, color: C.ink, lineHeight: 1 }}>{value}</div>
      <div style={{ marginTop: 6, fontSize: 14, color: C.taupe }}>{label}</div>
    </div>
  );
};

/**
 * The design's share-of-total bar list — "Loyalty mix", "Method split",
 * "Payment mix" are the same shape three times over, so it lives here once.
 *
 * `items` is [{label, value, pct}]; `value` is whatever reads on the right
 * (a percentage, a count, an amount), `pct` drives the bar width only.
 */
export const BAR_COLORS = [C.brown, C.bronze, C.sand, "#e3d2b8"];

export const MeterList = ({ items, empty = "Nothing to show yet" }) => {
  if (!items || items.length === 0) {
    return <div style={{ marginTop: 16, fontSize: 14, color: C.taupeLight }}>{empty}</div>;
  }
  return (
    <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 12 }}>
      {items.map((it, i) => (
        <div key={it.label}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, color: C.slate }}>
            <span>{it.label}</span>
            <span>{it.value}</span>
          </div>
          <div style={{ marginTop: 6, height: 8, borderRadius: 4, background: "#f2e7d7" }}>
            <div
              style={{
                width: `${Math.max(0, Math.min(100, it.pct || 0))}%`,
                height: "100%",
                borderRadius: 4,
                background: it.color || BAR_COLORS[i % BAR_COLORS.length],
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

/** The quiet "…→" link the design pins to the bottom of a side panel. */
export const PanelLink = ({ onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      marginTop: "auto", paddingTop: 14, border: 0, background: "transparent",
      color: C.brown, fontSize: 14, fontWeight: 500, fontFamily: "inherit",
      cursor: "pointer", textAlign: "left", padding: "14px 0 0",
    }}
  >
    {children} →
  </button>
);

/** Segmented pill toggle (Today / This month, and similar). */
export const PillToggle = ({ options, value, onChange }) => (
  <div style={{ display: "flex", padding: 4, borderRadius: 21, background: "#efe2ce" }}>
    {options.map((o) => {
      const active = o.value === value;
      return (
        <span
          key={o.value}
          onClick={() => onChange && onChange(o.value)}
          style={{
            padding: "8px 16px", borderRadius: 17, fontSize: 14, cursor: "pointer",
            background: active ? C.brown : "transparent",
            color: active ? "#fff" : C.taupe,
          }}
        >
          {o.label}
        </span>
      );
    })}
  </div>
);

/** Rounded search field used in the console topbar. */
export const SearchPill = ({ value, onChange, placeholder = "Search", width = 240 }) => (
  <div
    style={{
      display: "flex", alignItems: "center", gap: 10, height: 42, padding: "0 16px",
      borderRadius: 21, background: "#fff", border: `1px solid ${C.border}`, width,
    }}
  >
    <span className="ms" style={{ fontSize: 19, color: C.taupeLight }}>search</span>
    <input
      value={value}
      onChange={(e) => onChange && onChange(e.target.value)}
      placeholder={placeholder}
      style={{
        flex: 1, border: 0, outline: "none", background: "transparent",
        fontSize: 14, color: C.brownDeep, fontFamily: "inherit", minWidth: 0,
      }}
    />
  </div>
);

/** Status chip. Tone maps to the design's semantic colours. */
export const Chip = ({ tone = "neutral", children }) => {
  const tones = {
    neutral: { bg: C.tint, fg: C.brown },
    good: { bg: "rgba(76,175,80,.14)", fg: "#2f7d33" },
    warn: { bg: "rgba(224,165,63,.18)", fg: "#8a6115" },
    bad: { bg: "rgba(192,32,43,.12)", fg: C.red },
  };
  const t = tones[tone] || tones.neutral;
  return (
    <span
      style={{
        display: "inline-flex", alignItems: "center", gap: 5,
        padding: "4px 10px", borderRadius: 9, whiteSpace: "nowrap",
        background: t.bg, color: t.fg, fontSize: 13, fontWeight: 500,
      }}
    >
      {children}
    </span>
  );
};

/** Primary / quiet buttons, matching the design's pill shape. */
export const Btn = ({ kind = "primary", icon, children, onClick, disabled, style }) => {
  const kinds = {
    primary: { background: C.brown, color: "#fff", border: "none" },
    quiet: { background: "#fff", color: C.brown, border: `1px solid ${C.border}` },
    danger: { background: C.red, color: "#fff", border: "none" },
    ghost: { background: "transparent", color: C.taupe, border: "none" },
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
        height: 42, padding: "0 18px", borderRadius: 21, fontSize: 14,
        fontFamily: "inherit", cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.55 : 1, ...kinds[kind], ...style,
      }}
    >
      {icon && <span className="ms" style={{ fontSize: 19 }}>{icon}</span>}
      {children}
    </button>
  );
};

/**
 * Quiet data table. `columns` is [{key, label, width, render}], `rows` the data.
 * Renders its own empty state, because a table that simply shows nothing when
 * empty reads as broken.
 */
export const Table = ({ columns, rows, empty = "Nothing here yet", rowKey = "_id", onRowClick }) => (
  <div style={{ overflowX: "auto" }}>
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
      <thead>
        <tr>
          {columns.map((c) => (
            <th
              key={c.key}
              style={{
                textAlign: c.align || "left", padding: "10px 12px",
                fontSize: 12, fontWeight: 500, letterSpacing: ".6px",
                color: C.taupeLight, textTransform: "uppercase",
                borderBottom: `1px solid ${C.tint}`, whiteSpace: "nowrap",
                width: c.width,
              }}
            >
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr>
            <td
              colSpan={columns.length}
              style={{ padding: "34px 12px", textAlign: "center", color: C.taupeLight }}
            >
              {empty}
            </td>
          </tr>
        ) : (
          rows.map((r, i) => (
            <tr
              key={r[rowKey] || i}
              onClick={() => onRowClick && onRowClick(r)}
              style={{
                borderBottom: `1px solid ${C.tint}`,
                cursor: onRowClick ? "pointer" : "default",
              }}
            >
              {columns.map((c) => (
                <td
                  key={c.key}
                  style={{ padding: "13px 12px", textAlign: c.align || "left", color: C.brownDeep }}
                >
                  {c.render ? c.render(r) : r[c.key]}
                </td>
              ))}
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

/** Four-across KPI row, the layout the dashboard and most list screens open with. */
export const StatRow = ({ children, columns = 4 }) => (
  <div style={{ display: "grid", gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: 18 }}>
    {children}
  </div>
);

/**
 * The quieter KPI tile the operations screens use — caption above, figure
 * below, an optional line of context under it. No icon: on a page whose tiles
 * all describe the same subject, an icon per tile is decoration, and the
 * handoff's transport and fleet screens drop it.
 *
 * `dark` fills the tile brown. The payments screen uses exactly one of these
 * per row, to mark the figure the whole screen is really about.
 */
export const MiniStat = ({ label, value, hint, tone, dark }) => {
  const tones = { warn: "#b26a00", bad: C.red, good: "#2f7d33" };
  return (
    <div
      style={{
        background: dark ? C.brown : "#fff",
        border: `1px solid ${dark ? C.brown : C.tint}`,
        borderRadius: 18,
        padding: 20,
      }}
    >
      <div style={{ fontSize: 13, color: dark ? "rgba(255,255,255,.66)" : C.taupe }}>{label}</div>
      <div
        style={{
          marginTop: 10, fontSize: 32, fontWeight: 500, lineHeight: 1,
          color: dark ? "#fff" : tones[tone] || C.ink,
        }}
      >
        {value}
      </div>
      {hint != null && hint !== "" && (
        <div style={{ marginTop: 8, fontSize: 13, color: dark ? C.sand : C.taupeLight }}>{hint}</div>
      )}
    </div>
  );
};

/** Initials disc. The row anchor wherever a person is named. */
export const Avatar = ({ name, size = 34, solid }) => {
  const initials =
    (name || "")
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => w.charAt(0))
      .join("")
      .toUpperCase() || "?";
  return (
    <span
      style={{
        width: size, height: size, borderRadius: size / 2, flex: `0 0 ${size}px`,
        background: solid ? C.brown : C.tint, color: solid ? "#fff" : C.brown,
        display: "grid", placeItems: "center",
        fontSize: Math.round(size * 0.38), fontWeight: 700,
      }}
    >
      {initials}
    </span>
  );
};

/**
 * The flat pill row that heads a table — a filter over one collection, so it
 * reads as a set of tabs rather than the beige-tracked PillToggle used for a
 * dimension switch. `right` takes whatever sits at the far end of the bar.
 */
export const TabPills = ({ tabs, value, onChange, right }) => (
  <div
    style={{
      display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
      padding: "14px 22px", borderBottom: `1px solid ${C.panel}`,
    }}
  >
    {tabs.map((t) => (
      <span
        key={t.value}
        onClick={() => onChange && onChange(t.value)}
        style={{
          padding: "8px 15px", borderRadius: 17, fontSize: 14, cursor: "pointer",
          background: t.value === value ? C.brown : "transparent",
          color: t.value === value ? "#fff" : C.taupe,
        }}
      >
        {t.label}
      </span>
    ))}
    {right && (
      <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 }}>{right}</div>
    )}
  </div>
);

/**
 * Modal chrome. reactstrap owns the dialog (so isOpen/toggle wiring is
 * untouched) but its Bootstrap 4 skin is not the console's — this restyles the
 * shell and the form controls inside it. Drop <style>{MODAL_CSS}</style> once
 * per page and give the Modal className="con-modal".
 */
export const MODAL_CSS = `
.con-modal .modal-content{border:0;border-radius:22px;background:#fdf9f3;
  box-shadow:0 24px 60px rgba(0,0,0,.35);overflow:hidden}
.con-modal .modal-header{border:0;padding:26px 28px 0;align-items:flex-start}
.con-modal .modal-header .modal-title{font-size:20px;font-weight:500;color:${C.ink}}
.con-modal .modal-header .close{font-size:26px;font-weight:400;color:${C.taupeLight};
  opacity:1;padding:22px 26px;text-shadow:none}
.con-modal .modal-body{padding:22px 28px}
.con-modal .modal-footer{border:0;padding:6px 28px 26px;gap:12px}
.con-modal .form-control,.con-modal .custom-select{height:46px;padding:0 15px;background:#fff;
  border:1px solid #e6d8c4;border-radius:12px;font-size:15px;color:${C.ink};box-shadow:none}
.con-modal .form-control:focus{border-color:${C.sand};box-shadow:none}
.con-modal textarea.form-control{height:auto;min-height:76px;padding:12px 15px;line-height:1.5;font-size:14px}
.con-modal label{font-size:12px;letter-spacing:.6px;text-transform:uppercase;color:${C.taupe};margin-bottom:6px}
.con-modal .form-check label,.con-modal label.form-check-label{text-transform:none;letter-spacing:0;
  font-size:14px;color:${C.slate}}
.con-modal .form-group{margin-bottom:16px}
.con-modal hr{border-color:${C.tint}}
`;

/**
 * Round icon control — the design's quiet row action, where a full labelled
 * button would shout over the data it sits beside. Always give it a `title`:
 * a bare glyph with no accessible name is a guess, not a control.
 */
export const IconBtn = ({ icon, title, onClick, tone, disabled }) => {
  const colors = { bad: C.red, warn: "#b26a00" };
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={onClick}
      disabled={disabled}
      style={{
        width: 32, height: 32, borderRadius: 16, border: `1px solid ${C.border}`,
        background: "#fff", color: colors[tone] || C.brown,
        display: "grid", placeItems: "center", padding: 0,
        cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.5 : 1,
      }}
    >
      <span className="ms" style={{ fontSize: 18 }}>{icon}</span>
    </button>
  );
};

/** Section heading inside a panel or a modal. */
export const FieldLabel = ({ children, style }) => (
  <div
    style={{
      fontSize: 12, letterSpacing: ".6px", textTransform: "uppercase",
      color: C.taupe, marginBottom: 6, ...style,
    }}
  >
    {children}
  </div>
);

/**
 * Uppercase caption above a control — the editor screens' basic unit
 * (E3 · coupon editor: "CODE", "DISCOUNT TYPE", "MIN ORDER").
 */
export const Field = ({ label, hint, error, children, style }) => (
  <div style={{ minWidth: 0, ...style }}>
    {label && (
      <div
        style={{
          fontSize: 12, letterSpacing: ".6px", textTransform: "uppercase",
          color: C.taupe, marginBottom: 7,
        }}
      >
        {label}
      </div>
    )}
    {children}
    {error ? (
      <div style={{ marginTop: 6, fontSize: 12, color: C.red }}>{error}</div>
    ) : (
      hint && <div style={{ marginTop: 6, fontSize: 12, color: C.taupeLight }}>{hint}</div>
    )}
  </div>
);

/**
 * The design's pill switch (coupon editor "Rules" rows, attraction "Featured").
 * Brown when on, sand when off.
 */
export const Toggle = ({ on, onClick, title, disabled }) => (
  <span
    role="switch"
    aria-checked={!!on}
    aria-label={title}
    title={title}
    onClick={disabled ? undefined : onClick}
    style={{
      width: 46, height: 26, borderRadius: 13, flex: "0 0 auto",
      background: on ? C.brown : "#e3d2b8",
      padding: 3, boxSizing: "border-box", display: "flex",
      justifyContent: on ? "flex-end" : "flex-start",
      cursor: onClick && !disabled ? "pointer" : "default",
      opacity: disabled ? 0.5 : 1,
    }}
  >
    <span style={{ width: 20, height: 20, borderRadius: 10, background: "#fff" }} />
  </span>
);

/**
 * Uploaded artwork. Stored image paths are unreliable on older records, so a
 * broken src is expected rather than exceptional — it falls back to a tinted
 * icon instead of the browser's torn-image glyph.
 */
export class Thumb extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: !props.src, src: props.src };
  }

  componentDidUpdate(prev) {
    if (prev.src !== this.props.src) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ failed: !this.props.src, src: this.props.src });
    }
  }

  render() {
    const { src, alt, icon = "image", height, width, radius = 0, muted } = this.props;
    const box = {
      width: width || "100%",
      height: height || "100%",
      objectFit: "cover",
      display: "block",
      borderRadius: radius,
      filter: muted ? "grayscale(.6)" : "none",
    };
    if (this.state.failed) {
      return (
        <div style={{ ...box, background: C.iconTint, display: "grid", placeItems: "center", color: C.sand }}>
          <span className="ms" style={{ fontSize: 30 }}>{icon}</span>
        </div>
      );
    }
    return <img src={src} alt={alt || ""} style={box} onError={() => this.setState({ failed: true })} />;
  }
}

/**
 * Row of filter pills — the shape every list screen in the handoff opens with
 * ("All · 18", "Tours · 7"). `options` is [{value, label, count}].
 */
export const ChipTabs = ({ options, value, onChange }) => (
  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
    {options.map((o) => {
      const active = o.value === value;
      return (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange && onChange(o.value)}
          style={{
            padding: "9px 18px", borderRadius: 19, fontSize: 14, cursor: "pointer",
            fontFamily: "inherit", lineHeight: 1.3,
            background: active ? C.brown : "#fff",
            color: active ? "#fff" : C.taupe,
            border: active ? "1px solid transparent" : `1px solid ${C.border}`,
          }}
        >
          {o.label}
          {o.count != null && ` · ${o.count}`}
        </button>
      );
    })}
  </div>
);

/**
 * Plain column chart. The handoff draws several of these; none of them have a
 * charting library behind them, and a div per bar is honest about that.
 * `data` is [{label, value}] — no interpolation, no synthesised series.
 */
export const BarChart = ({ data, height = 150, empty = "Nothing to chart yet", highlightIndex }) => {
  const max = Math.max(1, ...data.map((d) => Number(d.value) || 0));
  if (!data.length) {
    return (
      <div style={{ height, display: "grid", placeItems: "center", color: C.taupeLight, fontSize: 14 }}>
        {empty}
      </div>
    );
  }
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 10, height }}>
      {data.map((d, i) => (
        <div
          key={d.label + i}
          title={`${d.label}: ${d.value}`}
          style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", alignItems: "center", gap: 7, height: "100%", justifyContent: "flex-end" }}
        >
          <span style={{ fontSize: 12, color: C.taupe }}>{d.value}</span>
          <div
            style={{
              width: "100%",
              height: `${Math.max((Number(d.value) || 0) / max * 100, (Number(d.value) || 0) > 0 ? 4 : 1)}%`,
              borderRadius: "6px 6px 0 0",
              background: i === highlightIndex ? C.bronze : C.brown,
            }}
          />
          <span
            style={{
              fontSize: 11, color: C.taupeLight, maxWidth: "100%",
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}
          >
            {d.label}
          </span>
        </div>
      ))}
    </div>
  );
};

/**
 * A labelled progress bar — "Redemptions 184 / 500" in the coupon editor.
 */
export const Meter = ({ label, value, max, note }) => {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, color: C.slate }}>
        <span>{label}</span>
        <span style={{ color: C.ink }}>{max > 0 ? `${value} / ${max}` : value}</span>
      </div>
      <div style={{ marginTop: 8, height: 8, borderRadius: 4, background: "#f2e7d7" }}>
        <div style={{ width: `${pct}%`, height: "100%", borderRadius: 4, background: C.brown }} />
      </div>
      {note && <div style={{ marginTop: 6, fontSize: 12, color: C.taupeLight }}>{note}</div>}
    </div>
  );
};

/**
 * Modal shell on the design's dialog surface. Reactstrap keeps ownership of
 * isOpen/toggle so existing wiring is untouched; this only restyles the card.
 */
export const Dialog = ({ isOpen, toggle, size, children, width }) => (
  <Modal isOpen={isOpen} toggle={toggle} centered size={size} className="console-dialog">
    <div style={{ padding: 30, maxWidth: width, margin: width ? "0 auto" : undefined }}>{children}</div>
  </Modal>
);

/** Title row for a Dialog, with the close affordance the design shows. */
export const DialogHead = ({ title, subtitle, onClose }) => (
  <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: 22, fontWeight: 500, color: C.ink }}>{title}</div>
      {subtitle && <div style={{ marginTop: 4, fontSize: 14, color: C.taupe }}>{subtitle}</div>}
    </div>
    {onClose && (
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        style={{ border: 0, background: "transparent", color: C.taupeLight, cursor: "pointer", padding: 4, lineHeight: 1 }}
      >
        <span className="ms" style={{ fontSize: 22 }}>close</span>
      </button>
    )}
  </div>
);

/** Full-width pill button used at the foot of a dialog. */
export const DialogBtn = ({ kind = "quiet", children, onClick, disabled, type = "button" }) => {
  const kinds = {
    quiet: { background: "#fff", color: C.slate, border: "1px solid #e6d8c4" },
    primary: { background: C.brown, color: "#fff", border: 0 },
    danger: { background: C.red, color: "#fff", border: 0 },
  };
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={{
        flex: 1, height: 52, borderRadius: 26, fontFamily: "inherit", fontSize: 15,
        cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.6 : 1, ...kinds[kind],
      }}
    >
      {children}
    </button>
  );
};

/**
 * The handoff's destructive confirmation (G3 · "Delete WELCOME25?"): an icon
 * tile, a plain question, the consequence spelled out, and — where one exists —
 * the softer action offered beside the hard one.
 */
export const ConfirmDialog = ({
  isOpen, toggle, icon = "delete", tone = "danger",
  title, body, tip, confirmText = "Delete", onConfirm,
  altText, onAlt, cancelText = "Cancel", loading,
}) => {
  const tint = tone === "danger"
    ? { bg: "#fbe9e9", fg: C.red }
    : { bg: C.iconTint, fg: C.brown };
  return (
    <Dialog isOpen={isOpen} toggle={toggle}>
      <div style={{ textAlign: "center" }}>
        <span className="ms" style={{ fontSize: 30, color: tint.fg, background: tint.bg, padding: 16, borderRadius: 20 }}>
          {icon}
        </span>
        <div style={{ marginTop: 20, fontSize: 21, fontWeight: 500, color: C.ink }}>{title}</div>
        {body && <div style={{ marginTop: 10, fontSize: 15, color: C.slate, lineHeight: 1.55 }}>{body}</div>}
        {tip && (
          <div style={{ marginTop: 22, padding: "14px 16px", borderRadius: 14, background: "#fbf0dd", fontSize: 13, color: C.taupe, textAlign: "left" }}>
            {tip}
          </div>
        )}
        <div style={{ marginTop: 26, display: "flex", gap: 12 }}>
          <DialogBtn onClick={onAlt || toggle} disabled={loading}>
            {altText || cancelText}
          </DialogBtn>
          <DialogBtn kind={tone === "danger" ? "danger" : "primary"} onClick={onConfirm} disabled={loading}>
            {loading ? "Working…" : confirmText}
          </DialogBtn>
        </div>
        {altText && (
          <button
            type="button"
            onClick={toggle}
            disabled={loading}
            style={{ marginTop: 14, border: 0, background: "transparent", color: C.taupe, fontSize: 14, fontFamily: "inherit", cursor: "pointer" }}
          >
            {cancelText}
          </button>
        )}
      </div>
    </Dialog>
  );
};

/**
 * Pager for list screens.
 *
 * Several lists rendered every row they had - the guest directory was drawing
 * all 2,565 customers at once. Pass `total`/`limit` for a server-paginated
 * list, or omit them to page an array already in hand.
 *
 * Renders nothing when there is only one page, so short lists stay clean.
 */
export const Pager = ({ page, totalPages, total, limit, onPage, onLimit, noun = "rows" }) => {
  const pages = totalPages || 1;
  const from = total ? (page - 1) * limit + 1 : 0;
  const to = total ? Math.min(page * limit, total) : 0;

  // A window of at most 5 page numbers around the current page, so 103 pages
  // does not render 103 buttons.
  const span = [];
  const start = Math.max(1, Math.min(page - 2, pages - 4));
  for (let i = start; i < start + 5 && i <= pages; i += 1) span.push(i);

  const btn = (active, disabled) => ({
    minWidth: 34, height: 34, padding: "0 10px", borderRadius: 10, fontSize: 13,
    fontFamily: "inherit", cursor: disabled ? "default" : "pointer",
    border: `1px solid ${active ? C.brown : C.tint}`,
    background: active ? C.brown : "#fff",
    color: active ? "#fff" : disabled ? C.taupeLight : C.brownDeep,
    opacity: disabled ? 0.5 : 1,
  });

  return (
    <div
      style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 14, padding: "14px 4px 2px", flexWrap: "wrap",
      }}
    >
      <div style={{ fontSize: 13, color: C.taupe }}>
        {total ? `Showing ${from}-${to} of ${total} ${noun}` : `Page ${page} of ${pages}`}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {onLimit && (
          <select
            value={limit}
            onChange={(e) => onLimit(Number(e.target.value))}
            style={{
              height: 34, borderRadius: 10, border: `1px solid ${C.tint}`,
              background: "#fff", color: C.brownDeep, fontSize: 13,
              fontFamily: "inherit", padding: "0 8px",
            }}
          >
            {[25, 50, 100].map((n) => (
              <option key={n} value={n}>{n} per page</option>
            ))}
          </select>
        )}
        {pages > 1 && (
          <React.Fragment>
            <button type="button" style={btn(false, page <= 1)}
              onClick={() => page > 1 && onPage(page - 1)}>Prev</button>
            {span.map((n) => (
              <button key={n} type="button" style={btn(n === page, false)}
                onClick={() => n !== page && onPage(n)}>{n}</button>
            ))}
            <button type="button" style={btn(false, page >= pages)}
              onClick={() => page < pages && onPage(page + 1)}>Next</button>
          </React.Fragment>
        )}
      </div>
    </div>
  );
};
